// react
import { useSelector } from 'react-redux';

// internationalization
import { useTranslation } from 'react-i18next';

// tanstack
import { createFileRoute, Link } from '@tanstack/react-router';

// material ui icons
import AddRoundedIcon from '@mui/icons-material/AddRounded';

// material ui
import { Button } from '@mui/material';

// API
import { useGetAlarmGroupsByAccountQuery } from '@/api/recalc/endpoints/alarmGroupsApiSlice';

// shared components
import PageContainer from '@/components/layout/PageContainer';
import PageHeader from '@/components/layout/PageHeader';
import GatewayLoadingIndicator from '@/components/ui/progress/GatewayLoadingIndicator';

// feature-components
import AlarmGroupDataGrid from '@/features/alarm-hub/components/alarm-group/AlarmGroupDataGrid';

// components
import { useSessionAuth } from '@/contexts/SessionAuthContext';
import { useAlertProvider } from '@/hooks/useAlertProvider';
import { getSelectedAccount } from '@/redux/selectedAccountSlice';

type AlarmGroupSubscriptionShape = {
  User?: {
    UserId?: number;
  };
};

type AlarmGroupShape = {
  AlarmSubscriptions?: AlarmGroupSubscriptionShape[];
};

export const Route = createFileRoute(
  '/$accountId/_user/energyxplorer/alarms/advanced/',
)({
  component: RouteComponent,
});

function RouteComponent() {
  const selectedAccount = useSelector(getSelectedAccount);
  const { t } = useTranslation(['alarms', 'energy-analysis']);

  const { session } = useSessionAuth();
  const sessionUserId = Number(session?.claims?.sub);

  const { data: alarmGroups = [], isLoading: isAlarmGroupsLoading } =
    useGetAlarmGroupsByAccountQuery(selectedAccount?.AccountId, {
      skip: !selectedAccount?.AccountId,
    });

  const typedAlarmGroups = alarmGroups as AlarmGroupShape[];

  const isUserSubscribed =
    !Number.isFinite(sessionUserId) || typedAlarmGroups.length === 0
      ? true
      : typedAlarmGroups.some((group) =>
          group.AlarmSubscriptions?.some(
            (subscription) => subscription.User?.UserId === sessionUserId,
          ),
        );

  useAlertProvider({
    enabled: !isUserSubscribed,
    severity: 'info',
    message: t('alarmGroups.notSubscribed', { ns: 'alarms' }),
  });

  return (
    <PageContainer maxWidth="xl" sx={{ py: 2 }}>
      <GatewayLoadingIndicator isLoading={isAlarmGroupsLoading} />
      <PageHeader
        title={t('alarm_setup.advanced_action', { ns: 'energy-analysis' })}
        disableArrow
      >
        <Button
          variant="contained"
          component={Link}
          to="create"
          startIcon={<AddRoundedIcon />}
        >
          {t('toolbar.new_alarm_group', {
            defaultValue: 'New',
          })}
        </Button>
      </PageHeader>

      <AlarmGroupDataGrid
        alarmGroups={alarmGroups}
        userOptions={selectedAccount?.UserRoles}
        editPathBase="/$accountId/energyxplorer/alarms/advanced"
      />
    </PageContainer>
  );
}
